import React, { useState, useEffect } from 'react';
import { string, shape as shapeType, func, number } from 'prop-types';
import {
  arrayOf,
  params,
  shape,
  string as stringType,
  customType,
  useLazyDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import {
  Button,
  ButtonGroup,
  DrawerBody,
  DrawerFooter,
  FormController,
  FormLabel,
  TextField,
  SkeletonBlock
} from '@one-thd/sui-atomic-components';
import { ProductInfo } from '../shared/ProductInfo';
import { ERROR } from '../shared/Constants';

export const CreateNewQuoteDrawer = ({
  itemId,
  quantity,
  customerInfo,
  price,
  fulfillmentMethod,
  fulfillmentLocation,
  localStoreId,
  setError,
  setQuoteName,
  setSuccess,
  onClose,
  setCreateNewQuoteDrawer,
  setErrorDrawer,
}) => {
  const [quoteNameError, setQuoteNameError] = useState(null);
  const [newQuoteName, setNewQuoteName] = useState('');
  const [showLoader, setShowLoader] = useState(false);

  const { svocId, userId } = customerInfo;

  const [
    createQuote,
    { data: createData, loading: createLoading, error: createError }
  ] = useLazyDataModel('createQuote', {
    variables: {
      svocId,
      userId,
      lineItems: [{
        itemId,
        quantity,
        fulfillmentLocation,
        fulfillmentMethod
      }],
      localStoreId,
      cartName: newQuoteName,
    },
    ssr: false,
    refetchQueries: [
      'getQuoteList'
    ]
  });

  const createANewQuote = () => {
    setShowLoader(true);
    createQuote();
  };

  const getValidationError = (value) => {
    const validCharacters = /^[a-zA-Z0-9\s]*$/;
    const isBlank = value.length === 0;
    let isValid = validCharacters.test(value);
    if (!isValid || isBlank) {
      if (isBlank) {
        setQuoteNameError(ERROR.EMPTY_LIST);
      } else {
        setQuoteNameError(ERROR.INVALID_LIST_NAME);
      }
    } else {
      setQuoteNameError(null);
    }
  };

  const triggerAnalytics = () => {
    const quoteId = createData?.createQuote?.quoteId;
    window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.newQuoteAdd', {
      quoteType: 'online',
      quoteId,
      item: {
        quoteLocation: 'pip',
        quantity,
        price: {
          basePrice: price
        },
        productInfo: {
          sku: itemId
        }
      }
    });
  };

  useEffect(() => {
    if (createData && !createLoading && !createError) {
      setQuoteName(newQuoteName);
      setNewQuoteName('');
      setCreateNewQuoteDrawer(false);
      setShowLoader(false);
      setSuccess(true);
      triggerAnalytics();
    } else if (createError) {
      setShowLoader(false);
      setError(createError);
      setCreateNewQuoteDrawer(false);
      setErrorDrawer(true);
    }
  }, [createData, createLoading, createError]);

  if (showLoader) {
    return (
      <DrawerBody>
        <SkeletonBlock />
      </DrawerBody>
    );
  }

  return (
    <>
      <DrawerBody>
        <ProductInfo itemId={itemId} />
        <FormController className="sui-px-6 sui-pt-6">
          <FormLabel id="create-quote" className="sui-cursor-default sui-text-base">
            Create Quote Name:
          </FormLabel>
          <div className="sui-pt-2">
            <TextField
              fullWidth
              placeholder="Enter a Quote Name"
              value={newQuoteName}
              onChange={(evt) => {
                setNewQuoteName(evt.target.value);
                getValidationError(evt.target.value);
              }}
              status={quoteNameError ? 'error' : null}
              statusMessage={quoteNameError}
            />
          </div>
        </FormController>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={createANewQuote}
            disabled={!newQuoteName?.length || !!quoteNameError}
            data-testid="create-new-quote"
          >
            Add To Quote
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

CreateNewQuoteDrawer.propTypes = {
  customerInfo: shapeType({
    svocId: string.isRequired,
    userId: string.isRequired,
  }).isRequired,
  price: number.isRequired,
  itemId: string.isRequired,
  quantity: number.isRequired,
  fulfillmentLocation: string.isRequired,
  fulfillmentMethod: string.isRequired,
  localStoreId: string.isRequired,
  setError: func.isRequired,
  setQuoteName: func.isRequired,
  setSuccess: func.isRequired,
  onClose: func.isRequired,
  setCreateNewQuoteDrawer: func.isRequired,
  setErrorDrawer: func.isRequired,
};

CreateNewQuoteDrawer.displayName = 'CreateNewQuoteDrawer';

CreateNewQuoteDrawer.dataModel = extend(
  {
    createQuote: params({
      svocId: stringType().isRequired(),
      userId: stringType().isRequired(),
      lineItems: arrayOf(
        customType('LineItemInput!').shape({
          itemId: stringType(),
          quantity: stringType(),
          fulfillmentLocation: stringType(),
          fulfillmentMethod: stringType()
        })
      ).isRequired(),
      localStoreId: stringType().isRequired(),
      cartName: stringType().isRequired()
    }).mutation().shape({
      quoteId: stringType(),
      errorData: arrayOf(
        shape({
          errors: arrayOf(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType()
                })
              })
            })
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType()
        })
      )
    }),
  },
  ProductInfo
);